import React, { useMemo } from 'react';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useCfoOpenIssues } from '../../../hooks/analytics/useCfoReports.ts';
import { StatCard, WidgetCard } from '../controller/shared.tsx';
import { CHART_AXIS_LINE, CHART_AXIS_TICK, CHART_GRID_STROKE, CHART_LEGEND_STYLE, CHART_TOOLTIP_STYLE, chartColorFor } from '../theme.ts';

const CLASS_COLORS: Record<string, string> = {
  material_weakness: chartColorFor('material_weakness'),
  significant_deficiency: chartColorFor('significant_deficiency'),
  control_deficiency: chartColorFor('control_deficiency'),
  unclassified: chartColorFor('unclassified'),
};

/**
 * Report #3 — Open Issues at a Glance. KPI tiles + bar-by-department/classification.
 * `deptFilter`, when set, restricts the department breakdown chart to a single
 * department (the summary tiles stay org-wide — they're a single aggregate
 * object, not per-row data).
 */
export const OpenIssuesPanel: React.FC<{ deptFilter?: string | null }> = ({ deptFilter }) => {
  const { data, isLoading, isError, error, dataUpdatedAt, isFetching, refetch } = useCfoOpenIssues();
  const summary = data?.summary;
  const byDept = useMemo(() => {
    const base = data?.by_department ?? [];
    return deptFilter ? base.filter((r) => (r.department ?? '') === deptFilter) : base;
  }, [data, deptFilter]);

  const classifications = useMemo(
    () => Array.from(new Set(byDept.map((r) => r.classification ?? 'unclassified'))),
    [byDept]
  );

  const chartData = useMemo(() => {
    const byDeptMap = new Map<string, Record<string, number | string>>();
    byDept.forEach((row) => {
      const dept = row.department ?? 'Unassigned';
      const entry = byDeptMap.get(dept) ?? { department: dept };
      const key = row.classification ?? 'unclassified';
      entry[key] = ((entry[key] as number) ?? 0) + row.count;
      byDeptMap.set(dept, entry);
    });
    return Array.from(byDeptMap.values());
  }, [byDept]);

  const isEmpty = !isLoading && !isError && byDept.length === 0 && !summary;

  return (
    <WidgetCard
      title="Open Issues at a Glance"
      description="Open, escalated, and repeat issues by department and classification"
      loading={isLoading}
      error={isError ? error : null}
      errorMessage={error?.message ?? 'Failed to load open issues.'}
      isEmpty={isEmpty}
      emptyMessage="No open issues recorded."
      updatedAt={dataUpdatedAt}
      onRefresh={refetch}
      isFetching={isFetching}
    >
      <div className="analytics-kpi-grid gap-3 mb-4">
        <StatCard title="Open" value={summary?.open_count ?? 0} color="bg-blue-100 text-blue-600" />
        <StatCard title="Escalated" value={summary?.escalated_count ?? 0} color="bg-red-100 text-red-600" />
        <StatCard title="Repeat" value={summary?.repeat_count ?? 0} color="bg-amber-100 text-amber-600" />
        <StatCard title="Overdue Action Plans" value={summary?.overdue_action_plans ?? 0} color="bg-purple-100 text-purple-600" />
      </div>
      {chartData.length > 0 && (
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 10, right: 10, bottom: 10, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_STROKE} vertical={false} />
              <XAxis dataKey="department" tick={CHART_AXIS_TICK} axisLine={CHART_AXIS_LINE} tickLine={false} />
              <YAxis tick={CHART_AXIS_TICK} axisLine={CHART_AXIS_LINE} tickLine={false} allowDecimals={false} />
              <Tooltip contentStyle={CHART_TOOLTIP_STYLE} cursor={{ fill: 'rgba(148, 163, 184, 0.08)' }} />
              <Legend wrapperStyle={CHART_LEGEND_STYLE} />
              {classifications.map((c, idx) => (
                <Bar
                  key={c}
                  dataKey={c}
                  name={c.replace(/_/g, ' ')}
                  stackId="cls"
                  fill={CLASS_COLORS[c] ?? chartColorFor(c)}
                  radius={idx === classifications.length - 1 ? [4, 4, 0, 0] : undefined}
                  maxBarSize={48}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </WidgetCard>
  );
};

export default OpenIssuesPanel;
